import React, { useState, useEffect, useRef } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import axios from "axios";
import Snackbar from "@mui/material/Snackbar";
import { Menu } from 'primereact/menu';

const apiUrl = process.env.REACT_APP_API_URL;

export default function DevisStockageDialog({ visible, onHide, ficheData, onDevisGenerated, forceNew = false }) {
  // États pour les informations du devis
  const [typeStockage, setTypeStockage] = useState("box");
  const [volume, setVolume] = useState(4); // Minimum 4m³
  const [prixUnitaire, setPrixUnitaire] = useState(13); // 13€/m³
  const [dureeStockage, setDureeStockage] = useState(1);
  const [remise, setRemise] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [devisInfo, setDevisInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const menuRef = useRef(null);

  // États pour l'édition des infos client
  const [hasChanges, setHasChanges] = useState(false);
  const [tempNom, setTempNom] = useState("");
  const [tempEmail, setTempEmail] = useState("");
  const [tempTel, setTempTel] = useState("");
  const [tempVille, setTempVille] = useState("");
  const [tempVolume, setTempVolume] = useState("");
  const [tempDisposition, setTempDisposition] = useState("");
  const [tempDuree, setTempDuree] = useState("");
  const [tempNote, setTempNote] = useState("");

  // Extraire le volume de la dimension (ex: "8m3" -> 8)
  // Fonction pour extraire le volume de la dimension
  const extractVolume = (dimension) => {
    if (!dimension) return 4;

    const dimensionStr = dimension.toLowerCase().trim();

    const containerMatch = dimensionStr.match(
      /(\d+)\s*(?:pieds?|ft)\s*(\d+)\s*m3/,
    );
    if (containerMatch) {
      const volume = parseFloat(containerMatch[2]);
      return isNaN(volume) ? 4 : Math.max(volume, 4);
    }

    const containerMatch2 = dimensionStr.match(/container\s*(\d+)\s*m3/);
    if (containerMatch2) {
      const volume = parseFloat(containerMatch2[1]);
      return isNaN(volume) ? 4 : Math.max(volume, 4);
    }

    const boxMatch = dimensionStr.match(/box\s*(\d+)\s*m3/);
    if (boxMatch) {
      const volume = parseFloat(boxMatch[1]);
      return isNaN(volume) ? 4 : Math.max(volume, 4);
    }

    const simpleMatch = dimensionStr.match(/(\d+)\s*m3/);
    if (simpleMatch) {
      const volume = parseFloat(simpleMatch[1]);
      return isNaN(volume) ? 4 : Math.max(volume, 4);
    }

    if (
      dimensionStr.includes("moins") ||
      dimensionStr.includes("petit") ||
      dimensionStr.includes("mini")
    ) {
      return 4;
    }

    const fallbackMatch = dimensionStr.match(/(\d+)/);
    if (fallbackMatch) {
      const volume = parseFloat(fallbackMatch[1]);
      return isNaN(volume) ? 4 : Math.max(volume, 4);
    }

    return 4;
  };

  // Extraire la durée (ex: "+6 mois" -> 6, "1/3 mois" -> 1, "3/6 mois" -> 3)
  const extractDuree = (duree) => {
    if (!duree) return 1;
    const match = duree.match(/(\d+)/);
    if (match) {
      const d = parseFloat(match[0]);
      return isNaN(d) ? 1 : d;
    }
    return 1;
  };

  // Initialiser les données du client
  useEffect(() => {
    if (ficheData) {
      setTempNom(ficheData.nom || "");
      setTempEmail(ficheData.mail || "");
      setTempTel(ficheData.num || "");
      setTempVille(ficheData.ville || "");
      setTempVolume(ficheData.dimension || "");
      setTempDisposition(ficheData.disposition || "");
      setTempDuree(ficheData.duree || "");
      setTempNote(ficheData.note || "");
      setHasChanges(false);

      // Initialiser le volume du devis
      const extractedVolume = extractVolume(ficheData.dimension);
      setVolume(extractedVolume);

      // Initialiser la durée
      const extractedDuree = extractDuree(ficheData.duree);
      setDureeStockage(extractedDuree || 1);
    }
  }, [ficheData]);

  // Vérifier les changements
  useEffect(() => {
    if (ficheData) {
      const hasAnyChange =
        tempNom !== (ficheData.nom || "") ||
        tempEmail !== (ficheData.mail || "") ||
        tempTel !== (ficheData.num || "") ||
        tempVille !== (ficheData.ville || "") ||
        tempVolume !== (ficheData.dimension || "") ||
        tempDisposition !== (ficheData.disposition || "") ||
        tempDuree !== (ficheData.duree || "");

      setHasChanges(hasAnyChange);
    }
  }, [
    tempNom,
    tempEmail,
    tempTel,
    tempVille,
    tempVolume,
    tempDisposition,
    tempDuree,
    ficheData,
  ]);

  // Vérifier si un devis existe déjà
  useEffect(() => {
    if (visible && ficheData?.idFiche && !forceNew) {
      checkExistingDevis();
    } else if (visible && forceNew) {
      setDevisInfo(null);
      setLoading(false);
    }
  }, [visible, ficheData, forceNew]);

  const checkExistingDevis = async () => {
    setLoading(true);
    setDevisInfo(null);

    if (forceNew) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(
        `${apiUrl}/IHM/api/stockage_devis.php?fiche_id=${ficheData.idFiche}`,
      );
      if (response.data.a_devis == 1) {
        setDevisInfo({
          a_devis: response.data.a_devis,
          numero_devis: response.data.dernier_devis_id,
          lien_devis: response.data.lien_devis,
          lien_pdf: response.data.lien_pdf,
          token_devis: response.data.token_devis,
          montant_devis: response.data.montant_total || 0,
          date_devis: response.data.date_devis || new Date().toISOString(),
          idVosFactures: response.data.idVosFactures,
          statut_devis: response.data.statut_devis || "brouillon",
          type_stockage: response.data.type_stockage || "box",
          volume: response.data.volume || volume,
          duree: response.data.duree || dureeStockage,
          prix_unitaire: response.data.prix_unitaire || prixUnitaire,
        });
      } else {
        setDevisInfo(null);
      }
    } catch (error) {
      console.error("Erreur lors de la vérification du devis:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => setOpenSnackbar(false);

  // Calculs du devis

  const volumeCalcule = Math.max(volume, 4);
  const sousTotalStockage = volumeCalcule * prixUnitaire * dureeStockage;

  // Calculer le total des options
  const totalOptions = options.reduce(
    (sum, opt) => sum + (parseFloat(opt.prix) || 0),
    0,
  );

  // Sous-total = stockage + options
  const sousTotal = sousTotalStockage + totalOptions;

  const montantRemise = (sousTotal * remise) / 100;
  const total = sousTotal - montantRemise;

  // Ajouter une option
  const ajouterOption = () => {
    setOptions([...options, { titre: "", description: "", prix: 0 }]);
  };

  const supprimerOption = (index) => {
    const newOptions = [...options];
    newOptions.splice(index, 1);
    setOptions(newOptions);
  };

  const modifierOption = (index, champ, valeur) => {
    const newOptions = [...options];
    newOptions[index][champ] = valeur;
    setOptions(newOptions);
  };

  // Générer le devis
  const handleGenererDevis = async () => {
    if (!ficheData) return;
    setGenerating(true);
    try {
      const response = await axios.post(
        `${apiUrl}/IHM/api/stockage_devis.php`,
        {
          fiche_id: ficheData.idFiche,
          nom: ficheData.nom,
          email: ficheData.mail,
          telephone: ficheData.num,
          ville: ficheData.ville,
          type_stockage: typeStockage,
          volume: volumeCalcule,
          prix_unitaire: prixUnitaire,
          duree: dureeStockage,
          remise: remise,
          options: options,
          is_new_version: forceNew,
          adminMod: localStorage.getItem("us"),
        },
      );

      if (response.data.success) {
        setDevisInfo({
          a_devis: 1,
          lien_devis: response.data.devis_url,
          lien_pdf: response.data.pdf_url,
          numero_devis: response.data.devis_number,
          montant_devis: response.data.montant,
          type_stockage: typeStockage,
          volume: volumeCalcule,
          duree: dureeStockage,
          prix_unitaire: prixUnitaire,
        });

        setSnackbarMessage("Devis généré avec succès !");
        setOpenSnackbar(true);
        if (onDevisGenerated) onDevisGenerated();
      } else {
        setSnackbarMessage(
          response.data.message || "Erreur lors de la génération",
        );
        setOpenSnackbar(true);
      }
    } catch (error) {
      console.error("Erreur:", error);
      setSnackbarMessage("Erreur lors de la génération du devis");
      setOpenSnackbar(true);
    } finally {
      setGenerating(false);
    }
  };

  // Sauvegarder les modifications client
  const handleSaveClient = async () => {
    try {
      await axios.post(`${apiUrl}/IHM/api/update_fiche_stock.php`, {
        idFiche: ficheData.idFiche,
        nom: tempNom,
        telephone: tempTel,
        email: tempEmail,
        ville: tempVille,
        volume: tempVolume,
        disposition: tempDisposition,
        duree: tempDuree,
        note: tempNote,
        adminMod: localStorage.getItem("us"),
      });

      setHasChanges(false);
      setSnackbarMessage("Informations client mises à jour");
      setOpenSnackbar(true);

      if (onDevisGenerated) onDevisGenerated();
    } catch (error) {
      console.error("Erreur de mise à jour :", error);
      setSnackbarMessage("Erreur lors de la sauvegarde");
      setOpenSnackbar(true);
    }
  };

  // Changer l'état de la fiche
  const handleEtatChange = async (newEtat) => {
    if (!newEtat) return;

    try {
      const data = {
        idFiche: ficheData.idFiche,
        etat: newEtat,
        note: "",
        adminMod: localStorage.getItem("us"),
      };

      const response = await axios.put(`${apiUrl}/IHM/api/fiche.php`, data);
      if (response.data) {
        setSnackbarMessage(`État changé avec succès`);
        setOpenSnackbar(true);
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } catch (error) {
      console.error("Erreur:", error);
      setSnackbarMessage("Erreur lors du changement d'état");
      setOpenSnackbar(true);
    }
  };

  // Menu des états
  const menuItems = [
    { label: "En cours", command: () => handleEtatChange("0") },
    { label: "Suivis", command: () => handleEtatChange("1") },
    { label: "Validé", command: () => handleEtatChange("7") },
    { label: "Stockage", command: () => handleEtatChange("8") },
    { label: "Abandonnées", command: () => handleEtatChange("3") },
  ];

  // Header du dialog
  const header = (
    <div className="flex justify-content-between align-items-center w-full">
      <span className="font-semibold text-lg">
        Fiche client & devis stockage
      </span>
      {/* <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <Button
          icon="pi pi-sync"
          text
          rounded
          size="small"
          onClick={(e) => menuRef.current?.toggle(e)}
          className="p-button-text no-focus-outline"
          style={{ 
            width: "32px", 
            height: "32px",
            background: "#f9fafb",
            color: "#4b5563"
          }}
        />
        <Menu model={menuItems} popup ref={menuRef} />
      </div> */}
    </div>
  );

  return (
    <>
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message={snackbarMessage}
        action={
          <IconButton
            size="small"
            color="inherit"
            onClick={handleCloseSnackbar}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />

      <Dialog
        blockScroll
        visible={visible}
        header={header}
        className="custom-dialog"
        style={{ width: "45vw", maxWidth: "700px" }}
        modal
        onHide={() => {
          onHide();
          setOptions([]);
        }}
        breakpoints={{ "960px": "90vw", "640px": "95vw" }}
      >
        <div className="p-3">
          {/* Section 1 - Informations client */}
          <div className="grid">
            <div className="col-12">
              <div
                style={{
                  background: "#f3f4f6",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  marginBottom: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "6px" }}
                >
                  <span
                    style={{
                      background: "#374151",
                      color: "white",
                      borderRadius: "50%",
                      width: "18px",
                      height: "18px",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "11px",
                      fontWeight: 700,
                    }}
                  >
                    1
                  </span>
                  <span
                    style={{
                      fontSize: "11px",
                      fontWeight: 600,
                      color: "#374151",
                      letterSpacing: "0.05em",
                    }}
                  >
                    INFORMATIONS CLIENT
                  </span>
                </div>

                {hasChanges && (
                  <span
                    onClick={() => handleSaveClient()}
                    style={{
                      border: "1px solid #22c55e",
                      borderRadius: "50%",
                      color: "#fff",
                      width: "28px",
                      height: "28px",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      backgroundColor: "#22c55e",
                    }}
                  >
                    <SaveIcon sx={{ fontSize: 16 }} />
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Infos client */}
          <div className="grid">
            <div className="col-12 md:col-4">
              <label
                style={{
                  fontSize: "12px",
                  color: "#374151",
                  display: "block",
                  marginBottom: "2px",
                }}
              >
                Nom
              </label>
              <input
                value={tempNom}
                onChange={(e) => setTempNom(e.target.value)}
                style={{
                  width: "100%",
                  height: "36px",
                  padding: "0 8px",
                  border: "1px solid #d1d5db",
                  borderRadius: "5px",
                  background: "white",
                  fontSize: "14px",
                }}
              />
            </div>
            <div className="col-12 md:col-4">
              <label
                style={{
                  fontSize: "12px",
                  color: "#374151",
                  display: "block",
                  marginBottom: "2px",
                }}
              >
                Email
              </label>
              <input
                value={tempEmail}
                onChange={(e) => setTempEmail(e.target.value)}
                style={{
                  width: "100%",
                  height: "36px",
                  padding: "0 8px",
                  border: "1px solid #d1d5db",
                  borderRadius: "5px",
                  background: "white",
                  fontSize: "14px",
                }}
              />
            </div>
            <div className="col-12 md:col-4">
              <label
                style={{
                  fontSize: "12px",
                  color: "#374151",
                  display: "block",
                  marginBottom: "2px",
                }}
              >
                Téléphone
              </label>
              <input
                value={tempTel}
                onChange={(e) => setTempTel(e.target.value)}
                style={{
                  width: "100%",
                  height: "36px",
                  padding: "0 8px",
                  border: "1px solid #d1d5db",
                  borderRadius: "5px",
                  background: "white",
                  fontSize: "14px",
                }}
              />
            </div>
          </div>

          <div className="grid">
            <div className="col-12 md:col-4">
              <label
                style={{
                  fontSize: "12px",
                  color: "#374151",
                  display: "block",
                  marginBottom: "2px",
                }}
              >
                Ville
              </label>
              <input
                value={tempVille}
                onChange={(e) => setTempVille(e.target.value)}
                style={{
                  width: "100%",
                  height: "36px",
                  padding: "0 8px",
                  border: "1px solid #d1d5db",
                  borderRadius: "5px",
                  fontSize: "14px",
                }}
              />
            </div>
            <div className="col-12 md:col-4">
              <label
                style={{
                  fontSize: "12px",
                  color: "#374151",
                  display: "block",
                  marginBottom: "2px",
                }}
              >
                Volume demandé
              </label>
              <input
                value={tempVolume}
                onChange={(e) => setTempVolume(e.target.value)}
                style={{
                  width: "100%",
                  height: "36px",
                  padding: "0 8px",
                  border: "1px solid #d1d5db",
                  borderRadius: "5px",
                  fontSize: "14px",
                }}
              />
            </div>
            <div className="col-12 md:col-4">
              <label
                style={{
                  fontSize: "12px",
                  color: "#374151",
                  display: "block",
                  marginBottom: "2px",
                }}
              >
                Durée souhaitée
              </label>
              <input
                value={tempDuree}
                onChange={(e) => setTempDuree(e.target.value)}
                style={{
                  width: "100%",
                  height: "36px",
                  padding: "0 8px",
                  border: "1px solid #d1d5db",
                  borderRadius: "5px",
                  fontSize: "14px",
                }}
              />
            </div>
          </div>

          <div className="grid">
            <div className="col-12">
              <label
                style={{
                  fontSize: "12px",
                  color: "#374151",
                  display: "block",
                  marginBottom: "2px",
                }}
              >
                Disposition
              </label>
              <input
                value={tempDisposition}
                onChange={(e) => setTempDisposition(e.target.value)}
                style={{
                  width: "100%",
                  height: "36px",
                  padding: "0 8px",
                  border: "1px solid #d1d5db",
                  borderRadius: "5px",
                  fontSize: "14px",
                }}
              />
            </div>
          </div>

          <div className="grid">
            <div className="col-12">
              <label
                style={{
                  fontSize: "12px",
                  color: "#374151",
                  display: "block",
                  marginBottom: "2px",
                }}
              >
                Note
              </label>
              <textarea
                value={tempNote || ""}
                onChange={(e) => setTempNote(e.target.value)}
                rows={2}
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #d1d5db",
                  borderRadius: "5px",
                  fontSize: "14px",
                  resize: "vertical",
                }}
              />
            </div>
          </div>

          {/* Section 2 - Génération du devis */}
          {loading ? (
            <div className="flex justify-content-center p-5">
              <i
                className="pi pi-spin pi-spinner"
                style={{ fontSize: "2rem", color: "#2563eb" }}
              ></i>
            </div>
          ) : !devisInfo?.a_devis || forceNew ? (
            <>
              <div className="grid">
                <div className="col-12">
                  <div
                    style={{
                      background: "#f3f4f6",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      marginBottom: "10px",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <span
                      style={{
                        background: "#374151",
                        color: "white",
                        borderRadius: "50%",
                        width: "18px",
                        height: "18px",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "11px",
                        fontWeight: 700,
                      }}
                    >
                      2
                    </span>
                    <span
                      style={{
                        fontSize: "11px",
                        fontWeight: 600,
                        color: "#374151",
                        letterSpacing: "0.05em",
                      }}
                    >
                      GÉNÉRER LE DEVIS
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid">
                <div className="col-12">
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      alignItems: "flex-end",
                      flexWrap: "wrap",
                      width: "100%",
                      justifyContent: "space-between",
                    }}
                  >
                    <div style={{ flex: "0 0 auto" }}>
                      <label
                        style={{
                          fontSize: "11px",
                          color: "#374151",
                          display: "block",
                          marginBottom: "2px",
                        }}
                      >
                        Type
                      </label>
                      <select
                        value={typeStockage}
                        onChange={(e) => setTypeStockage(e.target.value)}
                        style={{
                          height: "35px",
                          padding: "0 6px",
                          border: "1px solid #d1d5db",
                          borderRadius: "5px",
                          fontSize: "13px",
                          background: "white",
                        }}
                      >
                        <option value="box">Box</option>
                        <option value="container">Container</option>
                      </select>
                    </div>
                    <div style={{ flex: "0 0 auto" }}>
                      <label
                        style={{
                          fontSize: "11px",
                          color: "#374151",
                          display: "block",
                          marginBottom: "2px",
                        }}
                      >
                        Volume (m³)
                      </label>
                      <input
                        type="number"
                        value={volume}
                        onChange={(e) =>
                          setVolume(parseFloat(e.target.value) || 4)
                        }
                        min={4}
                        step={1}
                        style={{
                          width: "80px",
                          height: "35px",
                          padding: "0 6px",
                          border: "1px solid #d1d5db",
                          borderRadius: "5px",
                          fontSize: "14px",
                        }}
                      />
                      <span style={{ fontSize: "10px", color: "#6b7280" }}>
                        min 4m³
                      </span>
                    </div>
                    <div style={{ flex: "0 0 auto" }}>
                      <label
                        style={{
                          fontSize: "11px",
                          color: "#374151",
                          display: "block",
                          marginBottom: "2px",
                        }}
                      >
                        Durée (mois)
                      </label>
                      <input
                        type="number"
                        value={dureeStockage}
                        onChange={(e) =>
                          setDureeStockage(parseInt(e.target.value) || 1)
                        }
                        min={1}
                        style={{
                          width: "80px",
                          height: "35px",
                          padding: "0 6px",
                          border: "1px solid #d1d5db",
                          borderRadius: "5px",
                          fontSize: "14px",
                        }}
                      />
                    </div>
                    <div style={{ flex: "0 0 auto" }}>
                      <label
                        style={{
                          fontSize: "11px",
                          color: "#374151",
                          display: "block",
                          marginBottom: "2px",
                        }}
                      >
                        Prix/m³/mois (€)
                      </label>
                      <input
                        type="number"
                        value={prixUnitaire}
                        onChange={(e) =>
                          setPrixUnitaire(parseFloat(e.target.value) || 13)
                        }
                        min={0}
                        step={0.5}
                        style={{
                          width: "100px",
                          height: "35px",
                          padding: "0 6px",
                          border: "1px solid #d1d5db",
                          borderRadius: "5px",
                          fontSize: "14px",
                        }}
                      />
                    </div>
                    <div style={{ flex: "0 0 auto" }}>
                      <label
                        style={{
                          fontSize: "11px",
                          color: "#374151",
                          display: "block",
                          marginBottom: "2px",
                        }}
                      >
                        Remise %
                      </label>
                      <input
                        type="number"
                        value={remise}
                        onChange={(e) =>
                          setRemise(parseFloat(e.target.value) || 0)
                        }
                        min={0}
                        max={100}
                        style={{
                          width: "70px",
                          height: "35px",
                          padding: "0 6px",
                          border: "1px solid #d1d5db",
                          borderRadius: "5px",
                          fontSize: "14px",
                        }}
                      />
                    </div>
                    <div style={{ flex: "0 0 auto" }}>
                      <label
                        style={{
                          fontSize: "11px",
                          color: "#374151",
                          display: "block",
                          marginBottom: "2px",
                        }}
                      >
                        Option
                      </label>
                      <button
                        onClick={ajouterOption}
                        style={{
                          width: "35px",
                          height: "35px",
                          border: "1px solid #d1d5db",
                          borderRadius: "5px",
                          background: "white",
                          fontSize: "20px",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Options */}
              <div className="grid">
                <div className="col-12">
                  {options.map((option, index) => (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        gap: "8px",
                        alignItems: "flex-end",
                        marginTop: "8px",
                      }}
                    >
                      <div style={{ flex: 2 }}>
                        <label
                          style={{
                            fontSize: "11px",
                            color: "#374151",
                            display: "block",
                            marginBottom: "2px",
                          }}
                        >
                          Titre
                        </label>
                        <input
                          value={option.titre}
                          onChange={(e) =>
                            modifierOption(index, "titre", e.target.value)
                          }
                          placeholder="Assurance, Transport, etc."
                          style={{
                            width: "100%",
                            height: "35px",
                            padding: "0 8px",
                            border: "1px solid #d1d5db",
                            borderRadius: "5px",
                            fontSize: "14px",
                          }}
                        />
                      </div>
                      <div style={{ flex: 2 }}>
                        <label
                          style={{
                            fontSize: "11px",
                            color: "#374151",
                            display: "block",
                            marginBottom: "2px",
                          }}
                        >
                          Description
                        </label>
                        <input
                          value={option.description}
                          onChange={(e) =>
                            modifierOption(index, "description", e.target.value)
                          }
                          placeholder="Description"
                          style={{
                            width: "100%",
                            height: "35px",
                            padding: "0 8px",
                            border: "1px solid #d1d5db",
                            borderRadius: "5px",
                            fontSize: "14px",
                          }}
                        />
                      </div>
                      <div style={{ width: "80px" }}>
                        <label
                          style={{
                            fontSize: "11px",
                            color: "#374151",
                            display: "block",
                            marginBottom: "2px",
                          }}
                        >
                          Prix €
                        </label>
                        <input
                          type="number"
                          value={option.prix}
                          onChange={(e) =>
                            modifierOption(
                              index,
                              "prix",
                              parseFloat(e.target.value) || 0,
                            )
                          }
                          placeholder="0"
                          min={0}
                          step={1}
                          style={{
                            width: "100%",
                            height: "35px",
                            padding: "0 8px",
                            border: "1px solid #d1d5db",
                            borderRadius: "5px",
                            fontSize: "14px",
                          }}
                        />
                      </div>
                      <div>
                        <button
                          onClick={() => supprimerOption(index)}
                          style={{
                            height: "35px",
                            width: "35px",
                            border: "1px solid #fca5a5",
                            borderRadius: "5px",
                            background: "white",
                            color: "#ef4444",
                            cursor: "pointer",
                            fontSize: "16px",
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Récapitulatif du devis */}
              <div className="grid">
                <div className="col-12">
                  <div
                    style={{
                      background: "#f5f3eb",
                      borderRadius: "5px",
                      padding: "8px 12px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        padding: "4px 0",
                        fontSize: "14px",
                      }}
                    >
                      <span>
                        Stockage ({volumeCalcule}m³ × {prixUnitaire}€ ×{" "}
                        {dureeStockage} mois)
                      </span>
                      <span>{sousTotalStockage.toFixed(2)} €</span>
                    </div>
                    {options.map((opt, idx) => (
                      <div
                        key={idx}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          padding: "4px 0",
                          fontSize: "14px",
                        }}
                      >
                        <span>{opt.titre || "Option"}</span>
                        <span>{opt.prix.toFixed(2)} €</span>
                      </div>
                    ))}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        padding: "6px 0",
                        borderTop: "1px solid #d1d5db",
                        marginTop: "4px",
                        fontWeight: 600,
                        fontSize: "14px",
                      }}
                    >
                      <span>Sous-total</span>
                      <span>{sousTotal.toFixed(2)} €</span>
                    </div>
                    {remise > 0 && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          padding: "4px 0",
                          fontSize: "14px",
                          color: "#ef4444",
                        }}
                      >
                        <span>Remise ({remise}%)</span>
                        <span>- {montantRemise.toFixed(2)} €</span>
                      </div>
                    )}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        padding: "6px 0",
                        borderTop: "1px solid #d1d5db",
                        marginTop: "4px",
                        fontWeight: 700,
                        fontSize: "15px",
                      }}
                    >
                      <span>Total TTC</span>
                      <span>{total.toFixed(2)} €</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bouton générer */}
              <div className="grid">
                <div className="col-12">
                  <Button
                    label={generating ? "Génération..." : "Générer le devis"}
                    icon={generating ? "pi pi-spin pi-spinner" : "pi pi-file"}
                    className="w-full"
                    style={{
                      background: "#dbeafe",
                      border: "1px solid #bfdbfe",
                      padding: "0.75rem",
                      borderRadius: "6px",
                      fontSize: "14px",
                      fontWeight: 500,
                      justifyContent: "center",
                      gap: "8px",
                      color: "#1d4ed8",
                    }}
                    onClick={handleGenererDevis}
                    disabled={
                      generating ||
                      volume < 4 ||
                      prixUnitaire <= 0 ||
                      dureeStockage <= 0
                    }
                  />
                </div>
              </div>
            </>
          ) : (
            // Devis existant
            <>
              <div className="grid">
                <div className="col-12">
                  <div
                    className="p-3 border-round-sm"
                    style={{ background: "#f9fafb", borderRadius: "5px" }}
                  >
                    <div className="flex justify-content-between align-items-center mb-2">
                      <span className="font-bold">Devis N° :</span>
                      <span>{devisInfo.numero_devis}</span>
                    </div>
                    <div className="flex justify-content-between align-items-center mb-2">
                      <span className="font-bold">Date de création :</span>
                      <span>
                        {new Date(devisInfo.date_devis).toLocaleDateString(
                          "fr-FR",
                        )}
                      </span>
                    </div>
                    <div className="flex justify-content-between align-items-center mb-2">
                      <span className="font-bold">Type :</span>
                      <span>
                        {devisInfo.type_stockage === "box"
                          ? "Box"
                          : "Container"}
                      </span>
                    </div>
                    <div className="flex justify-content-between align-items-center mb-2">
                      <span className="font-bold">Volume :</span>
                      <span>{devisInfo.volume} m³</span>
                    </div>
                    <div className="flex justify-content-between align-items-center mb-2">
                      <span className="font-bold">Durée :</span>
                      <span>{devisInfo.duree} mois</span>
                    </div>
                    <div className="flex justify-content-between align-items-center mb-2">
                      <span className="font-bold">Prix unitaire :</span>
                      <span>{devisInfo.prix_unitaire} €/m³/mois</span>
                    </div>
                    <div className="flex justify-content-between align-items-center mb-2">
                      <span className="font-bold">Montant total :</span>
                      <span>
                        {parseFloat(devisInfo.montant_devis).toFixed(2)} €
                      </span>
                    </div>
                    <div className="flex justify-content-between align-items-center">
                      <span className="font-bold">Statut :</span>
                      <span>
                        <span
                          className="p-tag"
                          style={{
                            background:
                              devisInfo.statut_devis === "envoye"
                                ? "#dcfce7"
                                : "#fef3c7",
                            color:
                              devisInfo.statut_devis === "envoye"
                                ? "#16a34a"
                                : "#d97706",
                            padding: "2px 8px",
                            borderRadius: "4px",
                            fontSize: "12px",
                          }}
                        >
                          {devisInfo.statut_devis === "envoye"
                            ? "Envoyé"
                            : "Brouillon"}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions sur le devis */}
              <div className="grid mt-3">
                <div className="col-12">
                  <div className="flex gap-2 justify-content-center flex-wrap">
                    {devisInfo.lien_pdf && (
                      <Button
                        label="PDF"
                        severity="danger"
                        icon="pi pi-download"
                        style={{
                          background: "#fecaca",
                          border: "1px solid #fca5a5",
                          padding: "0.75rem",
                          borderRadius: "6px",
                          fontSize: "14px",
                          fontWeight: 500,
                          justifyContent: "center",
                          gap: "8px",
                          color: "#dc2626",
                        }}
                        onClick={() =>
                          window.open(devisInfo.lien_pdf, "_blank")
                        }
                      />
                    )}
                    {devisInfo.lien_devis && (
                      <Button
                        label="Voir"
                        icon="pi pi-eye"
                        severity="info"
                        style={{
                          background: "#dbeafe",
                          border: "1px solid #bfdbfe",
                          padding: "0.75rem",
                          borderRadius: "6px",
                          fontSize: "14px",
                          fontWeight: 500,
                          justifyContent: "center",
                          gap: "8px",
                          color: "#1d4ed8",
                        }}
                        onClick={() =>
                          window.open(devisInfo.lien_devis, "_blank")
                        }
                      />
                    )}
                    <Button
                      label="Modifier"
                      severity="warning"
                      icon="pi pi-pencil"
                      style={{
                        background: "#fed7aa",
                        border: "1px solid #fdba74",
                        padding: "0.75rem",
                        borderRadius: "6px",
                        fontSize: "14px",
                        fontWeight: 500,
                        justifyContent: "center",
                        gap: "8px",
                        color: "#c2410c",
                      }}
                      onClick={() =>
                        window.open(
                          `https://unptittukenplus.vosfactures.fr/invoices/${devisInfo.idVosFactures}/edit`,
                          "_blank",
                        )
                      }
                    />
                    <Button
                      label="Envoyer le devis"
                      icon="pi pi-envelope"
                      severity="success"
                      style={{
                        background: "#dcfce7",
                        border: "1px solid #bbf7d0",
                        padding: "0.75rem",
                        borderRadius: "6px",
                        fontSize: "14px",
                        fontWeight: 500,
                        justifyContent: "center",
                        gap: "8px",
                        color: "#16a34a",
                      }}
                      onClick={() => {
                        axios
                          .post(`${apiUrl}/IHM/api/sendMailDevisStockage.php`, {
                            id: ficheData.idFiche,
                            adminMod: localStorage.getItem("us"),
                          })
                          .then((res) => {
                            if (res.data.success) {
                              setSnackbarMessage(
                                `Le devis a bien été envoyé à ${ficheData.mail}`,
                              );
                              setOpenSnackbar(true);
                              setDevisInfo({
                                ...devisInfo,
                                statut_devis: "envoye",
                              });

  
                              setTimeout(() => {
                                onHide();
                                setOptions([]);
                              }, 1500);

                            } else {
                              setSnackbarMessage(
                                res.data.message || "Envoi impossible",
                              );
                              setOpenSnackbar(true);
                            }
                          })
                          .catch((err) => {
                            console.error("Erreur envoi mail:", err);
                            setSnackbarMessage("Impossible d'envoyer le mail");
                            setOpenSnackbar(true);
                          });
                      }}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </Dialog>
    </>
  );
}